import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import {
  LuSearch, LuChevronLeft, LuChevronRight, LuPlay, LuCheckCircle2, LuAward, LuHome, LuX, LuAlertTriangle, LuGraduationCap, LuDownload, LuClock,
} from 'react-icons/lu';
import MyCoursePlayer from './MyCoursePlayer';
import { downloadCertificate } from './trainingCertificate';
import { errorMessage, fmtDate, fmtScore } from './trainingUtils';
import './training.css';

/**
 * Meus Treinamentos — vitrine do colaborador no estilo de plataforma de
 * streaming: perfil com progresso geral, busca, faixa "Continuar progresso"
 * (capas horizontais), coleções em carrossel (capas verticais) e
 * certificados. Só aparecem cursos publicados e liberados para a
 * empresa/departamento do usuário — o escopo é decidido pelo backend.
 */

const OTHER_COLLECTION = 'Outros cursos';

const currentUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.employee?.full_name || u.username || 'Colaborador';
  } catch {
    return 'Colaborador';
  }
};

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

/** Faixa horizontal com setas (desktop) e rolagem por toque (celular). */
function Rail({ title, subtitle, children }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };
  return (
    <section className="tv-section">
      <div className="tv-section-head">
        <div>
          <h2 className="tv-section-title">{title}</h2>
          {subtitle && <div className="tv-section-sub">{subtitle}</div>}
        </div>
        <div className="tv-arrows">
          <button type="button" aria-label="Voltar" onClick={() => scroll(-1)}><LuChevronLeft /></button>
          <button type="button" aria-label="Avançar" onClick={() => scroll(1)}><LuChevronRight /></button>
        </div>
      </div>
      <div className="tv-rail" ref={ref}>{children}</div>
    </section>
  );
}

function CourseFlags({ c }) {
  return (
    <div className="tv-flags">
      {c.enrollment?.status === 'approved' && <span className="tv-flag tv-flag--done"><LuCheckCircle2 /> Concluído</span>}
      {c.enrollment?.status === 'failed' && <span className="tv-flag tv-flag--failed">Não aprovado</span>}
      {c.overdue && <span className="tv-flag tv-flag--late"><LuAlertTriangle /> Prazo vencido</span>}
      {!c.overdue && c.mandatory && c.enrollment?.status !== 'approved' && <span className="tv-flag tv-flag--mandatory">Obrigatório</span>}
    </div>
  );
}

function LandscapeCard({ c, onOpen }) {
  const progress = c.enrollment?.progress_percent ?? 0;
  return (
    <button type="button" className="tv-card tv-card--landscape" onClick={() => onOpen(c.id)} title={c.title}>
      <div className="tv-card-art" style={c.cover_image ? { backgroundImage: `url("${c.cover_image}")` } : undefined}>
        {!c.cover_image && <span className="tv-card-fallback">{c.title}</span>}
        <CourseFlags c={c} />
        <span className="tv-card-play"><LuPlay /></span>
        {c.enrollment && <span className="tv-card-progress"><span style={{ width: `${progress}%` }} /></span>}
      </div>
      <div className="tv-card-title">{c.title}</div>
      <div className="tv-card-meta">
        {c.enrollment ? `${progress}% concluído` : 'Não iniciado'}
        {c.due_date && c.enrollment?.status !== 'approved' ? ` · prazo ${fmtDate(c.due_date)}` : ''}
      </div>
    </button>
  );
}

function PortraitCard({ c, onOpen }) {
  const art = c.cover_image_portrait || c.cover_image;
  const progress = c.enrollment?.progress_percent ?? 0;
  return (
    <button type="button" className="tv-card tv-card--portrait" onClick={() => onOpen(c.id)} title={c.title}>
      <div className="tv-card-art" style={art ? { backgroundImage: `url("${art}")` } : undefined}>
        <CourseFlags c={c} />
        {/* Com capa vertical própria a arte já traz o nome; nos demais casos o título vai sobre o degradê. */}
        <div className={`tv-card-overlay ${c.cover_image_portrait ? 'on-hover' : ''}`}>
          <div className="tv-card-overlay-title">{c.title}</div>
          <div className="tv-card-overlay-meta">
            {c.lesson_count} aula{c.lesson_count !== 1 ? 's' : ''}
            {c.workload_hours !== null ? ` · ${Number(c.workload_hours).toLocaleString('pt-BR')} h` : ''}
          </div>
        </div>
        <span className="tv-card-play"><LuPlay /></span>
        {c.enrollment && <span className="tv-card-progress"><span style={{ width: `${progress}%` }} /></span>}
      </div>
    </button>
  );
}

function CertificatesView({ courses, onOpen }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const approved = courses.filter((c) => c.enrollment?.status === 'approved');

  const handleDownload = async (c) => {
    setBusyId(c.id); setError('');
    try {
      // Dados do certificado vêm do backend (só para matrícula aprovada).
      const { data } = await api.get(`/my-training/courses/${c.id}`);
      await downloadCertificate(data);
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível gerar o certificado'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="tv-section">
      <div className="tv-section-head"><h2 className="tv-section-title">Meus certificados</h2></div>
      {error && <div className="tv-alert">{error}</div>}
      {!approved.length ? (
        <div className="tv-empty">
          <span className="tv-empty-icon"><LuAward size={30} /></span>
          <p>Você ainda não tem certificados.</p>
          <small>Conclua um curso com aprovação para emitir o certificado.</small>
        </div>
      ) : (
        <div className="tv-cert-grid">
          {approved.map((c) => (
            <div key={c.id} className="tv-cert">
              <div className="tv-cert-art" style={c.cover_image ? { backgroundImage: `url("${c.cover_image}")` } : undefined}>
                <LuAward className="tv-cert-badge" />
              </div>
              <div className="tv-cert-body">
                <div className="tv-card-title" style={{ whiteSpace: 'normal' }}>{c.title}</div>
                <div className="tv-card-meta">
                  Concluído em {fmtDate(c.enrollment.completed_at)}
                  {c.enrollment.final_score !== null ? ` · nota ${fmtScore(c.enrollment.final_score)}` : ''}
                </div>
                <div className="tv-cert-actions">
                  <button type="button" className="tv-btn tv-btn--primary" onClick={() => handleDownload(c)} disabled={busyId === c.id}>
                    <LuDownload /> {busyId === c.id ? 'Gerando…' : 'Baixar certificado'}
                  </button>
                  <button type="button" className="tv-btn" onClick={() => onOpen(c.id)}>Rever curso</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function MyTrainingPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState('home'); // home | certificates
  const [search, setSearch] = useState('');
  const name = useMemo(currentUserName, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/my-training/courses');
      setCourses(data);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar seus treinamentos'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => courses.filter((c) => !c.read_only), [courses]);
  const overallProgress = active.length
    ? Math.round(active.reduce((s, c) => s + (c.enrollment?.progress_percent ?? 0), 0) / active.length)
    : 0;
  const stats = {
    inProgress: courses.filter((c) => c.enrollment?.status === 'in_progress').length,
    done: courses.filter((c) => c.enrollment?.status === 'approved').length,
    overdue: courses.filter((c) => c.overdue).length,
  };

  const continuing = courses.filter((c) => c.enrollment?.status === 'in_progress');
  const mandatoryTodo = active.filter((c) => c.mandatory && !c.enrollment);
  const finished = courses.filter((c) => c.enrollment && c.enrollment.status !== 'in_progress');
  const collections = useMemo(() => {
    const map = new Map();
    courses.forEach((c) => {
      const key = c.category || OTHER_COLLECTION;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    // Coleções nomeadas em ordem alfabética; "Outros cursos" sempre por último.
    return [...map.entries()].sort(([a], [b]) => {
      if (a === OTHER_COLLECTION) return 1;
      if (b === OTHER_COLLECTION) return -1;
      return a.localeCompare(b, 'pt-BR');
    });
  }, [courses]);
  const onlyOthers = collections.length === 1 && collections[0][0] === OTHER_COLLECTION;

  const term = search.trim().toLowerCase();
  const results = term
    ? courses.filter((c) => `${c.title} ${c.description || ''} ${c.category || ''}`.toLowerCase().includes(term))
    : [];

  if (openId) {
    return <MyCoursePlayer courseId={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  return (
    <div className="tv-home">
      <div className="tv-hero">
      <div className="tv-topbar">
        <div className="tv-profile">
          <div className="tv-avatar">{initials(name) || <LuGraduationCap />}</div>
          <div className="tv-profile-info">
            <div className="tv-profile-name">{name}</div>
            <div className="tv-profile-progress-label">Seu progresso <strong>{overallProgress}%</strong></div>
            <div className="tv-profile-progress"><span style={{ width: `${overallProgress}%` }} /></div>
          </div>
        </div>
        <div className="tv-search">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar cursos…" aria-label="Pesquisar cursos" />
          {search ? <button type="button" onClick={() => setSearch('')} aria-label="Limpar"><LuX /></button> : <LuSearch />}
        </div>
      </div>

      <div className="tv-nav">
        <button type="button" className={view === 'home' ? 'is-active' : ''} onClick={() => setView('home')}><LuHome /> Início</button>
        <button type="button" className={view === 'certificates' ? 'is-active' : ''} onClick={() => setView('certificates')}>
          <LuAward /> Certificados {stats.done > 0 && <span className="tv-nav-count">{stats.done}</span>}
        </button>
        <div className="tv-nav-stats">
          <span><LuClock /> {stats.inProgress} em andamento</span>
          <span><LuCheckCircle2 /> {stats.done} concluído{stats.done !== 1 ? 's' : ''}</span>
        </div>
      </div>
      </div>

      {error && <div className="tv-alert">{error}</div>}
      {stats.overdue > 0 && view === 'home' && !term && (
        <div className="tv-alert"><LuAlertTriangle /> Você tem {stats.overdue} curso{stats.overdue > 1 ? 's' : ''} com prazo de conclusão vencido.</div>
      )}

      {loading && !courses.length && <section className="tv-section"><div className="tv-empty"><p>Carregando…</p></div></section>}

      {!loading && !courses.length && !error && (
        <section className="tv-section">
          <div className="tv-empty">
            <span className="tv-empty-icon"><LuGraduationCap size={30} /></span>
            <p>Nenhum treinamento liberado para você ainda</p>
            <small>Quando a DHR Consultoria liberar um curso para sua equipe, ele aparece aqui.</small>
          </div>
        </section>
      )}

      {term && (
        <section className="tv-section">
          <div className="tv-section-head">
            <h2 className="tv-section-title">Resultados para “{search.trim()}”</h2>
          </div>
          {results.length ? (
            <div className="tv-grid">{results.map((c) => <LandscapeCard key={c.id} c={c} onOpen={setOpenId} />)}</div>
          ) : (
            <div className="tv-empty"><p>Nenhum curso encontrado.</p></div>
          )}
        </section>
      )}

      {!term && view === 'certificates' && <CertificatesView courses={courses} onOpen={setOpenId} />}

      {!term && view === 'home' && courses.length > 0 && (
        <>
          {continuing.length > 0 && (
            <Rail title="Continuar progresso">
              {continuing.map((c) => <LandscapeCard key={c.id} c={c} onOpen={setOpenId} />)}
            </Rail>
          )}
          {mandatoryTodo.length > 0 && (
            <Rail title="Obrigatórios para você" subtitle="Cursos que sua empresa definiu como obrigatórios">
              {mandatoryTodo.map((c) => <LandscapeCard key={c.id} c={c} onOpen={setOpenId} />)}
            </Rail>
          )}
          {collections.map(([title, list]) => (
            <Rail key={title} title={onlyOthers ? 'Todos os cursos' : title}
              subtitle={`${list.length} curso${list.length !== 1 ? 's' : ''}`}>
              {list.map((c) => <PortraitCard key={c.id} c={c} onOpen={setOpenId} />)}
            </Rail>
          ))}
          {finished.length > 0 && (
            <Rail title="Concluídos">
              {finished.map((c) => <LandscapeCard key={c.id} c={c} onOpen={setOpenId} />)}
            </Rail>
          )}
        </>
      )}
    </div>
  );
}
